/**
 * The load boundary as an Effect.
 *
 * `AudioPoolPlayer.load` is fire-and-forget with `onCanPlay`/`onError`
 * callbacks. `Effect.callback` makes their outcome the fiber's own result:
 * supersession interrupts the wait, the deadline is `Effect.timeoutOrElse`,
 * everything after "the chunk is playable" is straight-line code, and an
 * interrupted load leaves nothing behind that can fire against a later chunk.
 *
 * `onCanPlay` can fire synchronously — the web pool calls it inside `load()`
 * for an already-decoded element — and `resume` handles that directly.
 */
import { Duration, Effect } from 'effect';
import {
  AudioPoolPlaybackError,
  playbackLoadTimeoutError,
  type PlaybackLoadTimeoutError,
} from './playback-command';
import type { AudioPoolLoadOptions, AudioPoolPlayer } from './pool-player';

/** Only the transport call this needs — both the raw adapter and the React
 * handle satisfy it. */
type ChunkLoadTransport = Pick<AudioPoolPlayer, 'load'>;

/**
 * Loads `src` and succeeds when the chunk is playable.
 *
 * `onEnded` and `onPlaybackError` stay callbacks on purpose: they report later
 * events, not this load's outcome, and the work they drive — the chunk-boundary
 * advance, and recovery from a transport failure raised once the chunk is
 * already playing — has to outlive the command that started playback. Both
 * adapters keep the per-load `onError` registered for the life of the load, so
 * a failure that lands after `onCanPlay` reaches `onPlaybackError` instead of
 * an already-settled fiber.
 */
export const loadChunkOnPlayer = ({
  player,
  src,
  options,
  timeoutMs,
  onPlaybackError,
}: {
  readonly player: ChunkLoadTransport;
  readonly src: string;
  readonly options: AudioPoolLoadOptions;
  readonly timeoutMs: number;
  readonly onPlaybackError?:
    | ((error: AudioPoolPlaybackError) => void)
    | undefined;
}): Effect.Effect<void, PlaybackLoadTimeoutError | AudioPoolPlaybackError> =>
  Effect.callback<void, AudioPoolPlaybackError>((resume) => {
    let playable = false;
    player.load(src, {
      ...options,
      onCanPlay: () => {
        playable = true;
        resume(Effect.void);
      },
      onError: (error) => {
        const failure = AudioPoolPlaybackError.make(error);
        if (playable) {
          onPlaybackError?.(failure);
          return;
        }
        resume(Effect.fail(failure));
      },
    });
  }).pipe(
    Effect.timeoutOrElse({
      duration: Duration.millis(timeoutMs),
      orElse: () => Effect.fail(playbackLoadTimeoutError(timeoutMs)),
    }),
  );
